/**
 * The "Data Source" of a client import (#749), as the form holds it. Pure.
 *
 * The keys, labels and rules come from the shared constants, the ones the API
 * checks against: a pair the form accepts is a pair the server accepts.
 */
import {
  categoryTakesTool,
  type DataSourceCategory,
  dataSourceNeedsDetail,
  type ImportDataSourcePrefill,
  isDataSourceCategory,
  isToolOfCategory,
  type ListDataSourceInput,
} from '@shared/schemas';

export interface DataSourceDraft {
  category: DataSourceCategory | null;
  /** A tool key of the category, `other` included; null for a category without tools. */
  tool: string | null;
  /** The free text, as typed. */
  detail: string;
}

export const EMPTY_DATA_SOURCE: DataSourceDraft = { category: null, tool: null, detail: '' };

/** Whether the form shows the tool dropdown. */
export function showsToolPicker(draft: DataSourceDraft): boolean {
  return draft.category !== null && categoryTakesTool(draft.category);
}

/** Whether the form shows the free text, which is then required. */
export function showsDetailField(draft: DataSourceDraft): boolean {
  return draft.category !== null && dataSourceNeedsDetail(draft.category, draft.tool);
}

/**
 * The form's starting point from the licence's last import. A category the
 * constants no longer know gives nothing; a tool they no longer know (or that
 * left the category) gives the category alone.
 */
export function dataSourceFromPrefill(prefill: ImportDataSourcePrefill): DataSourceDraft | null {
  if (!prefill || !isDataSourceCategory(prefill.category)) return null;
  const { category } = prefill;
  if (categoryTakesTool(category)) {
    if (!prefill.tool || !isToolOfCategory(category, prefill.tool)) {
      return { ...EMPTY_DATA_SOURCE, category };
    }
    const detail = dataSourceNeedsDetail(category, prefill.tool) ? (prefill.detail ?? '') : '';
    return { category, tool: prefill.tool, detail };
  }
  const detail = dataSourceNeedsDetail(category, null) ? (prefill.detail ?? '') : '';
  return { category, tool: null, detail };
}

/**
 * The `data_source` of `POST /csv/import`: only what the pair asks for, the
 * text trimmed. Called once the draft passed `validateForAnalyze`.
 */
export function toDataSourcePayload(draft: DataSourceDraft): ListDataSourceInput | null {
  if (!draft.category) return null;
  const tool = categoryTakesTool(draft.category) ? draft.tool : null;
  return {
    category: draft.category,
    ...(tool ? { tool } : {}),
    ...(dataSourceNeedsDetail(draft.category, tool) ? { detail: draft.detail.trim() } : {}),
  };
}
